import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppContext } from '../context/AppContext';
import { Container, SectionShell, alpha, ease, fonts, hover, pressable, radius, text } from '../styles/GlobalStyles';
import SectionHeader from './common/SectionHeader';
import Reveal from './common/Reveal';
import { ChevronIcon, GitHubIcon, LinkedInIcon, MailIcon, MediumIcon, PinIcon, SendIcon } from './common/Icons';

const MIN_MESSAGE = 200;

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 0.85fr) minmax(0, 1.15fr);
  gap: 32px;
  align-items: start;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const GroupTitle = styled.div`
  padding: 0 16px 8px;
  font-size: ${text.xs};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${props => props.theme.colors.textTertiary};
`;

const Group = styled.div`
  border-radius: ${radius.lg};
  background: ${props => props.theme.colors.cardBackground};
  border: 1px solid ${props => props.theme.colors.border};
  overflow: hidden;

  & + ${GroupTitle} {
    margin-top: 28px;
  }
`;

const Row = styled(motion.a)`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  text-decoration: none;
  color: ${props => props.theme.colors.text};
  transition: background 0.2s ease;

  & + & > div {
    border-top: 1px solid ${props => props.theme.colors.border};
  }

  ${hover} {
    &:hover {
      background: ${props => props.theme.colors.backgroundAlt};
    }
  }

  &[href='#'] {
    cursor: default;
  }
`;

const RowIcon = styled.span`
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  color: ${props => props.theme.colors.textSecondary};
  background: ${props => props.theme.colors.backgroundAlt};
`;

const RowBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  margin: -14px 0;
  padding: 14px 0;

  span {
    flex: 1;
    min-width: 0;
  }

  small {
    display: block;
    font-size: ${text.xs};
    color: ${props => props.theme.colors.textTertiary};
  }

  strong {
    display: block;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  svg {
    flex-shrink: 0;
    color: ${props => props.theme.colors.textTertiary};
  }
`;

const Sheet = styled(motion.form)`
  position: relative;
  border-radius: 16px;
  background: ${props => props.theme.colors.cardBackground};
  border: 1px solid ${props => props.theme.colors.border};
  box-shadow: ${props => props.theme.shadows.medium};
  overflow: hidden;
`;

const Grabber = styled.div`
  width: 40px;
  height: 5px;
  margin: 8px auto 0;
  border-radius: 3px;
  background: ${props => props.theme.colors.borderStrong};
`;

const SheetHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  padding: 10px 16px 14px;

  h3 {
    font-size: ${text.base};
    font-weight: 700;
    color: ${props => props.theme.colors.text};
    text-align: center;
  }
`;

const Cancel = styled.button`
  justify-self: start;
  border: none;
  background: none;
  font-size: ${text.base};
  color: ${props => props.theme.colors.primaryText};
  cursor: pointer;
`;

const Send = styled.button<{ $ready: boolean }>`
  justify-self: end;
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  color: ${props => (props.$ready ? props.theme.colors.onPrimary : props.theme.colors.textTertiary)};
  background: ${props => (props.$ready ? props.theme.colors.primary : props.theme.colors.backgroundAlt)};
  cursor: pointer;
  ${pressable}
`;

const Field = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 18px;
  min-height: 50px;
  border-top: 1px solid ${props => props.theme.colors.border};
  cursor: text;

  > span {
    flex-shrink: 0;
    min-width: 64px;
    font-size: ${text.sm};
    color: ${props => props.theme.colors.textTertiary};
  }
`;

const Recipient = styled.span`
  padding: 4px 10px;
  border-radius: 999px;
  font-size: ${text.sm};
  font-weight: 500;
  color: ${props => props.theme.colors.primaryText};
  background: ${props => alpha(props.theme.colors.primary, 12)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Input = styled.input`
  flex: 1;
  min-width: 0;
  height: 50px;
  border: none;
  outline: none;
  background: transparent;
  font-size: ${text.base};
  font-family: inherit;
  color: ${props => props.theme.colors.text};

  &::placeholder {
    color: ${props => props.theme.colors.textTertiary};
    opacity: 0.7;
  }
`;

const Body = styled.div`
  position: relative;
  border-top: 1px solid ${props => props.theme.colors.border};
`;

const TextArea = styled.textarea`
  display: block;
  width: 100%;
  min-height: 220px;
  padding: 18px 18px 44px;
  border: none;
  outline: none;
  resize: vertical;
  background: transparent;
  font-size: ${text.base};
  line-height: 1.6;
  font-family: inherit;
  color: ${props => props.theme.colors.text};

  &::placeholder {
    color: ${props => props.theme.colors.textTertiary};
    opacity: 0.7;
  }
`;

const CounterPill = styled.span<{ $ok: boolean }>`
  position: absolute;
  right: 14px;
  bottom: 12px;
  padding: 3px 9px;
  border-radius: 999px;
  font-family: ${fonts.mono};
  font-size: ${text.xs};
  color: ${props => (props.$ok ? props.theme.colors.success : props.theme.colors.textTertiary)};
  background: ${props => (props.$ok ? alpha(props.theme.colors.success, 14) : props.theme.colors.backgroundAlt)};
  transition: all 0.25s ease;
`;

const Error = styled(motion.div)`
  padding: 0 18px 10px 92px;
  font-size: ${text.xs};
  color: ${props => props.theme.colors.error};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding-left: 18px;
  }
`;

type FieldName = 'name' | 'email' | 'subject' | 'message';
const emptyForm: Record<FieldName, string> = { name: '', email: '', subject: '', message: '' };

const Contact: React.FC = () => {
  const { content, language } = useAppContext();
  const { contact, personalInfo } = content;
  const tr = language === 'tr';
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState(emptyForm);

  const validateField = (name: FieldName, value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return contact.validation.required;
    switch (name) {
      case 'name':
        return trimmed.length < 2 ? contact.validation.minLength.replace('{min}', '2') : '';
      case 'email':
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? '' : contact.validation.email;
      case 'subject':
        return trimmed.length < 3 ? contact.validation.minLength.replace('{min}', '3') : '';
      case 'message':
        return trimmed.length < MIN_MESSAGE ? contact.validation.messageLength.replace('{current}', String(trimmed.length)) : '';
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const name = event.target.name as FieldName;
    setFormData(previous => ({ ...previous, [name]: event.target.value }));
    if (errors[name]) setErrors(previous => ({ ...previous, [name]: '' }));
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const name = event.target.name as FieldName;
    setErrors(previous => ({ ...previous, [name]: validateField(name, event.target.value) }));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const nextErrors = (Object.keys(formData) as FieldName[]).reduce(
      (all, name) => ({ ...all, [name]: validateField(name, formData[name]) }),
      emptyForm
    );
    setErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;

    const subject = `PORTFOLIO: ${formData.subject}`;
    const body = `Name: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`;
    window.location.href = `mailto:${personalInfo.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const messageLength = formData.message.trim().length;
  const ready = (Object.keys(formData) as FieldName[]).every(name => !validateField(name, formData[name]));

  const methods = [
    { label: contact.contactMethods[0].label, value: personalInfo.email, href: `mailto:${personalInfo.email}`, icon: <MailIcon size={17} /> },
    { label: contact.contactMethods[1].label, value: personalInfo.location, href: '#', icon: <PinIcon size={17} /> },
  ];

  const socials = [
    { label: 'LinkedIn', value: 'in/mehmetcansimsek', href: personalInfo.linkedin, icon: <LinkedInIcon size={16} /> },
    { label: 'GitHub', value: personalInfo.github.replace('https://github.com/', '@'), href: personalInfo.github, icon: <GitHubIcon size={16} /> },
    { label: 'Medium', value: personalInfo.medium.replace('https://medium.com/', ''), href: personalInfo.medium, icon: <MediumIcon size={16} /> },
  ];

  const field = (name: Exclude<FieldName, 'message'>, type = 'text') => (
    <>
      <Field htmlFor={name}>
        <span>{contact.form[name]}:</span>
        <Input
          id={name}
          name={name}
          type={type}
          value={formData[name]}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={contact.form.placeholders[name]}
          aria-invalid={!!errors[name]}
        />
      </Field>
      <AnimatePresence>
        {errors[name] && (
          <Error initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
            {errors[name]}
          </Error>
        )}
      </AnimatePresence>
    </>
  );

  return (
    <SectionShell id="contact">
      <Container>
        <SectionHeader index={5} title={contact.title} subtitle={contact.description} />

        <Layout>
          <Reveal>
            <GroupTitle>{content.navigation.contact}</GroupTitle>
            <Group>
              {methods.map(method => (
                <Row key={method.label} href={method.href} onClick={method.href === '#' ? event => event.preventDefault() : undefined}>
                  <RowIcon>{method.icon}</RowIcon>
                  <RowBody>
                    <span>
                      <small>{method.label}</small>
                      <strong>{method.value}</strong>
                    </span>
                    {method.href !== '#' && <ChevronIcon size={16} />}
                  </RowBody>
                </Row>
              ))}
            </Group>

            <GroupTitle>{tr ? 'Sosyal' : 'Social'}</GroupTitle>
            <Group>
              {socials.map(social => (
                <Row key={social.label} href={social.href} target="_blank" rel="noopener noreferrer">
                  <RowIcon>{social.icon}</RowIcon>
                  <RowBody>
                    <span>
                      <small>{social.label}</small>
                      <strong>{social.value}</strong>
                    </span>
                    <ChevronIcon size={16} />
                  </RowBody>
                </Row>
              ))}
            </Group>
          </Reveal>

          <Sheet
            onSubmit={handleSubmit}
            noValidate
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-64px' }}
            transition={{ duration: 0.5, delay: 0.06, ease: ease.out }}
          >
            <Grabber />
            <SheetHeader>
              <Cancel
                type="button"
                onClick={() => {
                  setFormData(emptyForm);
                  setErrors(emptyForm);
                }}
              >
                {tr ? 'Vazgeç' : 'Cancel'}
              </Cancel>
              <h3>{contact.formTitle}</h3>
              <Send type="submit" $ready={ready} aria-label={contact.form.submitButton} title={contact.form.submitButton}>
                <SendIcon size={17} />
              </Send>
            </SheetHeader>

            <Field as="div">
              <span>{tr ? 'Kime' : 'To'}:</span>
              <Recipient>{personalInfo.email}</Recipient>
            </Field>
            {field('name')}
            {field('email', 'email')}
            {field('subject')}

            <Body>
              <TextArea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={contact.form.placeholders.message}
                aria-label={contact.form.message}
                aria-invalid={!!errors.message}
              />
              <CounterPill $ok={messageLength >= MIN_MESSAGE}>
                {messageLength}/{MIN_MESSAGE}
              </CounterPill>
            </Body>
            <AnimatePresence>
              {errors.message && (
                <Error style={{ paddingLeft: 18 }} initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
                  {errors.message}
                </Error>
              )}
            </AnimatePresence>
          </Sheet>
        </Layout>
      </Container>
    </SectionShell>
  );
};

export default Contact;
